import React, { Suspense, useEffect } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { FmProgress, FmSpinner, FmToastProvider, useFmToast } from '@/components/ui';
import { useAppStore } from '@/stores/app';
import { LanguageProvider, useLanguage } from '@/i18n';
import { FmFormControlThemeProvider } from '@/shared/ui/FmFormControlThemeProvider';
import { AuthProvider } from '@/contexts/AuthProvider';
import { PropertySubscriptionProvider } from '@/contexts/PropertySubscriptionProvider';
import { CreditTopUpProvider } from '@/contexts/CreditTopUpContext';
import { SelectedPropertyProvider } from '@/contexts/SelectedPropertyContext';
import { useSelectedProperty } from '@/contexts/useSelectedProperty';
import { PublicRouteGate } from '@/contexts/PublicRouteGate';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import AppLayout from '@/components/AppLayout';
import LandingPage from '@/views/LandingPage/LandingPage';
import PropertyAccessGuard from '@/components/guards/PropertyAccessGuard';
import { setAuthFailNavigate, setAuthFailShowError } from '@/api/client';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import ChunkLoadErrorBoundary from '@/components/ChunkLoadErrorBoundary';
import { fmSpacing } from '@/shared/config/designTokens';
import {
  appRoutePath,
  appRoutePatterns,
  protectedRouteChildPath,
} from '@/shared/navigation/appRoutePatterns';
import { RouteParamKeyed } from '@/shared/navigation/RouteParamKeyed';
import { resolveDefaultCatalogRouteDestination } from '@/shared/navigation/catalogNavigation';
import { settingsSubscriptionPath } from '@/shared/navigation/landingHandoffContext';
import {
  canonicalOperationsPlanningNewPlanPath,
  resolveOperationsPlanningNewPlan,
} from '@/shared/navigation/operationsPlanningNavigation';
import {
  getRequiredCatalogRoute,
  resolveRouteQueryContext,
} from '@/shared/ui/FmRouteCatalog';
import { fmRouteCatalogDefault } from '@/shared/ui/FmRouteCatalogDefault';
// import StandSimulationView from '@/components/stand/StandSimulationView';
import '@/utils/simulationTest'; // Make test functions available globally

const MapView = React.lazy(() => lazyWithRetry(() => import('@/views/MapView')));
const AboutView = React.lazy(() => lazyWithRetry(() => import('@/views/AboutView')));
const StandDetailView = React.lazy(() => lazyWithRetry(() => import('@/views/StandDetailView')));
const ForestOverviewView = React.lazy(() => lazyWithRetry(() => import('@/views/ForestOverviewView')));
const PriceEstimateView = React.lazy(() => lazyWithRetry(() => import('@/views/PriceEstimateView')));
// Named loader so the MyForests dashboard chunk can be prefetched on idle (see
// the prefetch effect in <App/>) as well as lazily rendered. Warming the chunk
// ahead of navigation both speeds up the primary /app/my-forests route and
// prevents the in-flight dynamic import from being aborted (net::ERR_ABORTED)
// when a user opens my-forests and immediately navigates away before the chunk
// settles.
const loadMyForestsView = () => import('@/views/MyForestsView');
const MyForestsView = React.lazy(() => lazyWithRetry(loadMyForestsView));
const PropertyBrowseView = React.lazy(() => lazyWithRetry(() =>
  import('@/views/browse-properties/PropertyBrowseView')
));
const OperationsPlanningView = React.lazy(() => lazyWithRetry(() => import('@/views/OperationsPlanningView')));
const OperationsPlanningNewPlanView = React.lazy(() =>
  lazyWithRetry(() => import('@/views/OperationsPlanningNewPlanView')),
);
const OperationsPlanDraftBoundary = React.lazy(() =>
  lazyWithRetry(() =>
    import('@/contexts/OperationsPlanDraftBoundary').then((module) => ({
      default: module.OperationsPlanDraftBoundary,
    })),
  ),
);
const UserSettingsView = React.lazy(() => lazyWithRetry(() => import('@/views/UserSettingsView')));
const MeasurementDashboardView = React.lazy(() => lazyWithRetry(() => import('@/views/MeasurementDashboardView')));
const MeasurementPlotPropertyResolver = React.lazy(() =>
  lazyWithRetry(() => import('@/views/measurements/MeasurementPlotPropertyResolverView')),
);
const MeasurementPropertyStudioView = React.lazy(() => lazyWithRetry(() => import('@/views/MeasurementPropertyStudioView')));
const SimulationDebugger = React.lazy(() => lazyWithRetry(() => import('@/components/debug/SimulationDebugger')));
const DesignSystemView = React.lazy(() => lazyWithRetry(() => import('@/views/DesignSystemView')));
const VisualGrammarView = React.lazy(() => lazyWithRetry(() => import('@/views/VisualGrammarView')));
const OperationalServerStateContractView = React.lazy(() => lazyWithRetry(() => import('@/views/OperationalServerStateContractView')));
const E3ProductionSeamSmoke = React.lazy(() => lazyWithRetry(() => import('@/components/debug/E3ProductionSeamSmoke')));
const CreditTopUpBoundaryHarness = React.lazy(() => lazyWithRetry(() => import('@/components/debug/CreditTopUpBoundaryHarness')));
// Blank-page diagnosis probe: a route that really throws in
// render, so the browser lane can prove window.__FM_RENDER_THROWS__ is written on
// the production path. Armed only in dev / automated browsers.
const RenderThrowBoundaryHarness = React.lazy(() => lazyWithRetry(() => import('@/components/debug/RenderThrowBoundaryHarness')));
const TimberPriceReportRoute = React.lazy(() => lazyWithRetry(() => import('@/views/timber-prices/TimberPriceReportView')));
const ImplementView = React.lazy(() => lazyWithRetry(() => import('@/views/ImplementView')));
const OfferDecodeView = React.lazy(() => lazyWithRetry(() => import('@/views/offer-decode/OfferDecodeView')));
const OfferDecodeResultView = React.lazy(() => lazyWithRetry(() => import('@/views/offer-decode-result/OfferDecodeResultView')));
const SentryWatchView = React.lazy(() => lazyWithRetry(() => import('@/views/SentryWatchView')));
const SatelliteHistoryView = React.lazy(() => lazyWithRetry(() => import('@/views/SatelliteHistoryView')));
const ClimateHistoryView = React.lazy(() => lazyWithRetry(() => import('@/views/ClimateHistoryView')));
const BuyerDiligenceView = React.lazy(() => lazyWithRetry(() => import('@/views/BuyerDiligenceView')));
const NotFoundView = React.lazy(() => lazyWithRetry(() => import('@/views/NotFoundView')));
const VerifyEmailView = React.lazy(() => lazyWithRetry(() => import('@/views/VerifyEmailView')));
const DeploymentQualificationView = React.lazy(() => lazyWithRetry(() => import('@/views/DeploymentQualificationView')));

const RouteSpinner: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <FmSpinner size="lg" />
  </div>
);

const PropertyDetailsRedirect: React.FC = () => {
  const { propertyId } = useParams<{ propertyId: string }>();
  if (!propertyId) return <Navigate to={appRoutePatterns.map} replace />;
  return (
    <Navigate
      to={resolveDefaultCatalogRouteDestination(
        'forest-overview',
        { propertyId, returnTo: '' },
      )}
      replace
    />
  );
};

const LegacyVisualizationRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to={appRoutePatterns.map} replace />;
  return (
    <Navigate
      to={resolveDefaultCatalogRouteDestination(
        'forest-overview',
        { propertyId: id, returnTo: '' },
      )}
      replace
    />
  );
};

const OperationsPlanningQueryRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get('propertyId');
  if (propertyId) {
    return (
      <Navigate to={appRoutePath(appRoutePatterns.operationsPlanning, { propertyId })} replace />
    );
  }
  return <Navigate to={appRoutePatterns.map} replace />;
};

const CatalogPropertySelectionRedirect: React.FC<{ routeId: string }> = ({ routeId }) => {
  const location = useLocation();
  const { selectedProperty } = useSelectedProperty();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  return (
    <Navigate
      to={resolveDefaultCatalogRouteDestination(
        routeId,
        { propertyId: selectedProperty?.id ?? null, returnTo },
      )}
      replace
    />
  );
};

const CatalogQueryContextBoundary: React.FC<{
  routeId: string;
  children: React.ReactElement;
  /** Resolves a missing required forest from the routed record itself. */
  resolveMissingProperty?: React.ReactElement;
}> = ({ routeId, children, resolveMissingProperty }) => {
  const location = useLocation();
  const route = getRequiredCatalogRoute(fmRouteCatalogDefault, routeId);
  const resolution = resolveRouteQueryContext(route, location.search);
  if (resolution.kind === 'normalize') {
    return (
      <Navigate
        to={`${location.pathname}${resolution.search}${location.hash}`}
        replace
      />
    );
  }
  if (resolution.kind === 'missing-required') {
    if (resolveMissingProperty) return resolveMissingProperty;
    const targetRouteId = route.propertySwitchRouteId;
    if (!targetRouteId) {
      throw new Error(
        `Route "${route.id}" requires query property context without a governed property switch route.`,
      );
    }
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate
        to={resolveDefaultCatalogRouteDestination(
          targetRouteId,
          { propertyId: null, returnTo },
        )}
        replace
      />
    );
  }
  return children;
};

const MeasurementDashboardRoute: React.FC<{ routeId: string }> = ({
  routeId,
}) => (
  <CatalogQueryContextBoundary
    routeId={routeId}
    resolveMissingProperty={
      routeId === 'measurement-plot' ? <MeasurementPlotPropertyResolver /> : undefined
    }
  >
    <MeasurementDashboardView />
  </CatalogQueryContextBoundary>
);

const OperationsPlanningRoute: React.FC = () => {
  const { t } = useLanguage();
  return (
    <PropertyAccessGuard
      requiredTier="free"
      featureName={t.common.featureNames.operationsPlanning}
      description={t.common.featureNames.operationsPlanningDescription}
    >
      <OperationsPlanningView />
    </PropertyAccessGuard>
  );
};

const OperationsPlanningNewPlanRoute: React.FC = () => {
  const { t } = useLanguage();
  const { propertyId } = useParams<{ propertyId: string }>();
  const location = useLocation();
  const validHandoff = propertyId
    ? resolveOperationsPlanningNewPlan(location.search, propertyId) !== null
    : false;
  if (!validHandoff && propertyId) {
    return (
      <Navigate to={canonicalOperationsPlanningNewPlanPath(location.search, propertyId)} replace />
    );
  }
  return (
    <PropertyAccessGuard
      requiredTier="free"
      featureName={t.common.featureNames.operationsPlanning}
      description={t.common.featureNames.operationsPlanningDescription}
    >
      <OperationsPlanningNewPlanView />
    </PropertyAccessGuard>
  );
};

const StandDetailRoute: React.FC = () => {
  const { t } = useLanguage();
  return (
    <PropertyAccessGuard
      requiredTier="free"
      featureName={t.common.featureNames.standDetails}
      description={t.common.featureNames.standDetailsDescription}
    >
      <StandDetailView />
    </PropertyAccessGuard>
  );
};

const AntDLocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { language } = useLanguage();
  return (
    <FmFormControlThemeProvider theme="inherit" language={language}>
      {children}
    </FmFormControlThemeProvider>
  );
};

/**
 * Registers react-router navigate and FmToast error feedback with the
 * auth-fail cascade so it can navigate without a hard reload and surface
 * a user-visible reason before redirecting.  Renders nothing; must be
 * mounted inside BrowserRouter and FmToastProvider contexts.
 */
const AuthFailCoordinator: React.FC = () => {
  const navigate = useNavigate();
  const toast = useFmToast();

  useEffect(() => {
    setAuthFailNavigate((path, opts) => navigate(path, opts ?? {}));
    setAuthFailShowError((msg) => toast.error(msg));
    return () => {
      setAuthFailNavigate(null);
      setAuthFailShowError(null);
    };
  }, [navigate, toast]);

  return null;
};

function usePrefetchMyForests(): void {
  useEffect(() => {
    const win = window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    const prefetch = () => {
      // Route loading remains authoritative; an idle prefetch failure is non-fatal.
      void loadMyForestsView().catch(() => {});
    };
    if (typeof win.requestIdleCallback === 'function') {
      const handle = win.requestIdleCallback(prefetch);
      return () => win.cancelIdleCallback?.(handle);
    }
    const timer = window.setTimeout(prefetch, 1500);
    return () => window.clearTimeout(timer);
  }, []);
}

const App: React.FC = () => {
  const { isLoading, loadingMessage, progress, checkBackendHealth } = useAppStore();

  useEffect(() => {
    checkBackendHealth();
  }, [checkBackendHealth]);

  usePrefetchMyForests();

  return (
    <>
      <FmToastProvider>
        <AuthProvider>
          <PropertySubscriptionProvider>
            <LanguageProvider>
              <AntDLocaleProvider>
                <CreditTopUpProvider>
                    <SelectedPropertyProvider>
                      <AuthFailCoordinator />
                      <ChunkLoadErrorBoundary>
                        <Suspense fallback={<RouteSpinner />}>
                          <Routes>
                          <Route path="/" element={<PublicRouteGate authenticatedRedirect="/app" redirectOnlyFrom="/"><LandingPage /></PublicRouteGate>} />
                          <Route path="/pricing" element={<LandingPage />} />
                          {/* Email verification: public route, no auth required */}
                          <Route path="/verify-email" element={<VerifyEmailView />} />
                          <Route path="/debug/e3-production-seams" element={<E3ProductionSeamSmoke />} />
                          <Route path="/debug/credit-top-up-boundary/:propertyId" element={<CreditTopUpBoundaryHarness />} />
                          <Route path="/debug/render-throw-boundary" element={<RenderThrowBoundaryHarness />} />
                          {/* /login deep-link: redirect to home and open login modal */}
                          <Route path="/login" element={<Navigate to="/" state={{ openLogin: true }} replace />} />
                          <Route path="/auth/login" element={<Navigate to="/" state={{ openLogin: true }} replace />} />
                          <Route path="/sign-in" element={<Navigate to="/" state={{ openLogin: true }} replace />} />

                          {/* In-app subscription and pricing live in Settings, inside the app shell. */}
                          <Route path="/app/pricing" element={<Navigate to={settingsSubscriptionPath()} replace />} />
                          <Route path="/app/subscriptions" element={<Navigate to={settingsSubscriptionPath()} replace />} />
                          {/* Redirect old route to new route — uses PropertyDetailsRedirect to read the param */}
                          <Route path="/app/detailed-analysis/:propertyId" element={<PropertyDetailsRedirect />} />

                          {/* Protected App Routes — single ProtectedRoute parent keeps AppLayout mounted across navigations */}
                          <Route path={appRoutePatterns.map} element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                            <Route index element={<MapView />} />
                            <Route path={protectedRouteChildPath(appRoutePatterns.forestOverview)} element={<ForestOverviewView />} />
                            {/* Secure version: checks property access before showing valuation */}
                            <Route path={protectedRouteChildPath(appRoutePatterns.priceEstimate)} element={<PriceEstimateView />} />
                            <Route element={<OperationsPlanDraftBoundary />}>
                              <Route path={protectedRouteChildPath(appRoutePatterns.standDetail)} element={<StandDetailRoute />} />
                              <Route path={protectedRouteChildPath(appRoutePatterns.operationsPlanning)} element={<OperationsPlanningRoute />} />
                              <Route
                                path={protectedRouteChildPath(appRoutePatterns.operationsPlanningNewPlan)}
                                element={<OperationsPlanningNewPlanRoute />}
                              />
                            </Route>
                            <Route path={protectedRouteChildPath(appRoutePatterns.myForests)} element={<MyForestsView />} />
                            <Route
                              path={protectedRouteChildPath(appRoutePatterns.browseProperties)}
                              element={
                                <Suspense fallback={<RouteSpinner />}>
                                  <PropertyBrowseView />
                                </Suspense>
                              }
                            />
                            {/* Measurement workspace routes */}
                            <Route
                              path={protectedRouteChildPath(appRoutePatterns.measurements)}
                              element={<MeasurementDashboardRoute routeId="measurements" />}
                            />
                            <Route path={protectedRouteChildPath(appRoutePatterns.measurementProperty)} element={<MeasurementPropertyStudioView />} />
                            <Route
                              path={protectedRouteChildPath(appRoutePatterns.measurementCampaign)}
                              element={<MeasurementDashboardRoute routeId="measurement-campaign" />}
                            />
                            <Route
                              path={protectedRouteChildPath(appRoutePatterns.measurementPlot)}
                              element={<MeasurementDashboardRoute routeId="measurement-plot" />}
                            />
                            <Route path={protectedRouteChildPath(appRoutePatterns.timberPrices)} element={<TimberPriceReportRoute />} />
                            <Route
                              path="implement"
                              element={<CatalogPropertySelectionRedirect routeId="implement" />}
                            />
                            <Route path={protectedRouteChildPath(appRoutePatterns.implement)} element={<ImplementView />} />
                            <Route path={protectedRouteChildPath(appRoutePatterns.offerDecode)} element={<RouteParamKeyed param="propertyId" element={<OfferDecodeView />} />} />
                            <Route path={protectedRouteChildPath(appRoutePatterns.offerDecodeResult)} element={<RouteParamKeyed param="decodeId" element={<OfferDecodeResultView />} />} />
                            <Route path={protectedRouteChildPath(appRoutePatterns.watch)} element={<SentryWatchView />} />
                            <Route path={protectedRouteChildPath(appRoutePatterns.satelliteHistory)} element={<SatelliteHistoryView />} />
                            <Route path={protectedRouteChildPath(appRoutePatterns.climateHistory)} element={<ClimateHistoryView />} />
                            <Route path={protectedRouteChildPath(appRoutePatterns.buyerDiligence)} element={<BuyerDiligenceView />} />
                            <Route path={protectedRouteChildPath(appRoutePatterns.settings)} element={<UserSettingsView />} />
                            <Route path="debug/simulation" element={<SimulationDebugger />} />
                            <Route path="debug/design-system" element={<DesignSystemView />} />
                            <Route path="debug/visual-grammar" element={<VisualGrammarView />} />
                            <Route path="debug/server-state-contract" element={<OperationalServerStateContractView />} />
                            <Route path="deployment-qualification" element={<DeploymentQualificationView />} />
                            <Route path={protectedRouteChildPath(appRoutePatterns.about)} element={<AboutView />} />
                            {/* Legacy /app/property-overview paths inside protected tree */}
                            <Route path="property-overview/:propertyId" element={<PropertyDetailsRedirect />} />
                            <Route path="visualization/:id" element={<LegacyVisualizationRedirect />} />
                          </Route>

                          {/* Legacy query-param operations-planning URL */}
                          <Route path="/operations-planning" element={<OperationsPlanningQueryRedirect />} />

                          {/* Catch-all redirect for old URLs */}
                          <Route path="/property/:propertyId/details" element={<PropertyDetailsRedirect />} />
                          <Route path="/visualization/:id" element={<LegacyVisualizationRedirect />} />
                          <Route path="/property-overview/:propertyId" element={<PropertyDetailsRedirect />} />
                          <Route path="/my-forests" element={<Navigate to={appRoutePatterns.myForests} replace />} />
                          <Route path="/about" element={<Navigate to={appRoutePatterns.about} replace />} />

                          {/* Catch-all: show 404 page instead of blank screen */}
                          <Route path="*" element={<NotFoundView />} />
                          </Routes>
                        </Suspense>
                      </ChunkLoadErrorBoundary>
                    </SelectedPropertyProvider>
                  </CreditTopUpProvider>
              </AntDLocaleProvider>
            </LanguageProvider>
          </PropertySubscriptionProvider>
        </AuthProvider>
      </FmToastProvider>

      {/* Global Loading Overlay */}
      {isLoading && (
        <div className="progress-overlay">
          <div className="progress-card">
            <FmSpinner size="lg" />
            <h3 style={{ margin: `${fmSpacing.md}px 0 ${fmSpacing.sm}px 0` }}>
              {loadingMessage || 'Loading...'}
            </h3>
            {progress !== null && <FmProgress percent={progress} />}
          </div>
        </div>
      )}
    </>
  );
};

export default App;
